// Based on https://reactabular.js.org/

import * as React from 'react';
import ReactDOM from 'react-dom';
import { compose } from 'redux';
import {
  cloneDeep, findIndex, orderBy, keys, values, transform
} from 'lodash';
import * as Table from 'reactabular-table';
import * as search from 'searchtabular';
import * as sort from 'sortabular';
import * as resolve from 'table-resolver';
import VisibilityToggles from 'react-visibility-toggles';

import {
  Paginator, PrimaryControls, paginate
} from './helpers';
import { TABLE_DEFS } from './table_defs';
import DateExceptionsModal from '../Assessment/Rostering/date_exceptions_modal';
import DateExceptionPromptModal from '../Assessment/Rostering/date_exception_prompt_modal';

export default class AqueductTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      rows: props.rows,
      searchColumn: 'all',
      query: {}, // search query
      sortingColumns: props.first_column_sort && {0: {direction: props.first_column_sort, position: 0, tooltipText: props.selection_tooltip}}, // reference to the sorting columns
      columns: this.getColumns(), // initial columns
      pagination: { // initial pagination settings
        page: 1,
        perPage: 999999999
      },
      modalOpen: false, // TODO: remove this
      selectedStudent: null, // TODO: remove this
      dateExceptionPromptOpen: false, // TODO: remove this
      studentToSelect: null, // TODO: remove this
      unknownEmails: [],
      currentSortField: null,
      currentSortDirection: null
    };

    this.onRow = this.onRow.bind(this);
    this.onRowSelected = this.onRowSelected.bind(this);
    this.onColumnChange = this.onColumnChange.bind(this);
    this.onSearch = this.onSearch.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onPerPage = this.onPerPage.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.onToggleColumn = this.onToggleColumn.bind(this);
    this.toggleSelect = this.toggleSelect.bind(this);
    this.applySort = this.applySort.bind(this);
  }

  applySort(field) {
    let sortedRows = cloneDeep(this.state.rows).sort((a, b) => {
      if (a[field].toLowerCase() < b[field].toLowerCase()) return -1;
      if (a[field].toLowerCase() > b[field].toLowerCase()) return 1;
      return 0;
    });

    if (field !== this.state.currentSortField) {
      this.setState({ currentSortDirection: 'asc' });
    } else {
      if (this.state.currentSortDirection === 'asc') {
        sortedRows = cloneDeep(sortedRows).reverse();
        this.setState({ currentSortDirection: 'desc' });
      } else {
        this.setState({ currentSortDirection: 'asc' });
      }
    }
    
    this.setState({ rows: sortedRows, currentSortField: field });
  }


  componentDidMount() {
    // Some table cells (<td> elements) have children elements
    // that receive the <td> element's click events
    // when we would not like them to.
    // The only way to prevent those click events from reaching
    // the children is to stop propagation at the <td> level.
    // We identify such <td> elements by adding the "disable-change"
    // class to one of the <td> element's descendants.
    for (const el of ReactDOM.findDOMNode(this).getElementsByClassName('disable-change')) {
      el.closest('td').addEventListener('click', (event) => {
        event.stopPropagation();
      })
    }
  }

  getColumns() {
    return TABLE_DEFS[this.props.table_def].call(this);
  }

  toggleSelect(rowsToToggle, doSelect) {
    let rowIdsToToggle = rowsToToggle.map(row => row.id);
    let rows = cloneDeep(this.state.rows).map(row => {
      if (rowIdsToToggle.includes(row.id)) {
        row.selected = doSelect
      }
      return row;
    });

    this.setState({ rows });
  }

  gatherRows() {
    const {
      columns, rows, pagination, sortingColumns, searchColumn, query
    } = this.state;
    const cols = columns.filter(column => column.visible);
    return compose(
      paginate(pagination),
      sort.sorter({ columns: cols, sortingColumns, sort: orderBy }),
      search.highlighter({ columns: cols, matches: search.matches, query }),
      search.multipleColumns({ columns: cols, query }),
      resolve.resolve({
        columns: cols,
        method: (extra) => compose(
          resolve.byFunction('cell.resolve')(extra),
          resolve.nested(extra)
        )
      })
    )(rows);
  }

  // TODO: --------- REMOVE THESE, they don't belong here -----------------------
  openModal = (student) => {
    this.setState(
      {
        modalOpen: true,
        selectedStudent: student,
      }
    );
  };

  closeModal = () => {
    this.setState({ modalOpen: false, selectedStudent: null });
  };

  saveDateExceptions = (startDate, endDate) => {
    this.setState((prevState) => {
      const updatedRows = prevState.rows.map((row) => {
        if (row.id === prevState.selectedStudent.id) {
          return { ...row, date_exceptions: `${startDate} - ${endDate}` };
        }
        return row;
      });

      return {
        rows: updatedRows,
        modalOpen: false,
        selectedStudent: null,
      };
    });
  };

  closeDateExceptionPrompt = () => {
    this.setState({ 
      dateExceptionPromptOpen: false,
      studentToSelect: null 
    });
  };
  // -------------------------------------------------------------

  render() {
    const {
      columns, rows, pagination, sortingColumns, searchColumn, query
    } = this.state;
    const cols = columns.
      filter(column => column.visible).
      map(column => {
        if (column && column.property && (!('filterable' in column) || column.filterable)) {
          column.filterPlaceholder = 'type to filter';
        }
        return column;
      });
    const paginated = this.gatherRows();
    const windowTableName = this.props.window_table_name || 'AqueductTable';
    const tableHeight = this.props.table_height || 'fixed-height-table';
    const selectedStudents = paginated.rows.filter(row => row.selected)
    const totalCount = (this.state.unknownEmails?.length ?? 0) + (selectedStudents?.length ?? 0);

    return (
      <div ref={() => { window[windowTableName] = this }}>
        {this.props.selectable_columns &&
          <VisibilityToggles
            columns={columns}
            onToggleColumn={this.onToggleColumn}
          />
        }
        <PrimaryControls
          className="controls custom-course-controls"
          perPage={pagination.perPage}
          column={searchColumn}
          query={query}
          columns={cols}
          rows={rows}
          onPerPage={this.onPerPage}
          onColumnChange={this.onColumnChange}
          onSearch={this.onSearch}
          show_pagination={this.props.show_pagination}
          isCustomCoursePage={this.props.custom_course_page}
          studentsCount={totalCount}
          unknownEmails={this.state.unknownEmails}
          selectedRows={paginated.rows}
          toggleSelect={this.toggleSelect}
          psaEmail={this.props.psa_email}
        />

        <div className={tableHeight}>
          <div className="table-student-container">
            <div className="table-wrapper">
              <Table.Provider
                className="pure-table pure-table-striped"
                columns={cols}
              >
                <Table.Header id={windowTableName}>
                  <search.Columns query={query} columns={cols} onChange={this.onSearch} />
                </Table.Header>

                <Table.Body
                  onRow={this.onRow}
                  rows={paginated.rows}
                  rowKey="id"
                />

                <CustomFooter columns={cols} rows={paginated.rows} />
              </Table.Provider>
            </div>

            {this.props.custom_course_page && (
              <>
                {totalCount > 0 && (
                  <div className="vertical-divider" />
                )}
                <div className="desktop-student-list">
                  <RenderStudentList
                    unknownEmails={this.state.unknownEmails}
                    rows={paginated.rows}
                    toggleSelect={this.toggleSelect}
                    psaEmail={this.props.psa_email}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="controls">
          {this.props.show_pagination &&
            <Paginator
              pagination={pagination}
              pages={paginated.amount}
              onSelect={this.onSelect}
            />
          }
        </div>
        {this.state.modalOpen && (
          <DateExceptionsModal
            isOpen={this.state.modalOpen}
            onClose={this.closeModal}
            student={this.state.selectedStudent}
            onSave={this.saveDateExceptions}
          />
        )}
        {this.state.dateExceptionPromptOpen && (
          <DateExceptionPromptModal
            onSetDateExceptions={() => {
              const student = this.state.studentToSelect.rowsRequiringDateExceptions[0];
              this.setState({
                modalOpen: true,
                selectedStudent: student,
                dateExceptionPromptOpen: false
              });
            }}
            onCancel={this.closeDateExceptionPrompt}
          />
        )}
      </div>
    );
  }
  onRow(row, { rowIndex }) {
    return {
      className: row.row_class,
      'data-target' : this.props.row_target,
      onClick: () => this.onRowSelected(row)
    };
  }
  onRowSelected(row) {
    // console.log('clicked row', row);
  }
  onColumnChange(searchColumn) {
    this.setState({
      searchColumn
    });
  }
  onSearch(query) {
    this.setState({
      query
    });
  }
  onSelect(page) {
    const pages = Math.ceil(
      this.state.rows.length / this.state.pagination.perPage
    );

    this.setState({
      pagination: {
        ...this.state.pagination,
        page: Math.min(Math.max(page, 1), pages)
      }
    });
  }
  onPerPage(value) {
    this.setState({
      pagination: {
        ...this.state.pagination,
        perPage: parseInt(value, 10)
      }
    });
  }
  onRemove(id) {
    const rows = cloneDeep(this.state.rows);
    const idx = findIndex(rows, { id });

    // this could go through flux etc.
    rows.splice(idx, 1);

    this.setState({ rows });
  }
  onToggleColumn({ columnIndex }) {
    const columns = cloneDeep(this.state.columns);
    const column = columns[columnIndex];

    column.visible = !column.visible;

    const query = cloneDeep(this.state.query);
    delete query[column.property];

    this.setState({ columns, query });
  }
}

const CustomFooter = ({ columns, rows }) => {
  return (
    <tfoot>
    <tr>
      {columns.map((column, i) =>
        <td key={`footer-${i}`}>{column.footer ? column.footer(rows) : null}</td>
      )}
    </tr>
    </tfoot>
  );
}

export const RenderStudentList = ({ unknownEmails, rows, toggleSelect, psaEmail }) => {
  return (
    <ul>
      {unknownEmails && unknownEmails.length > 0 && (
        <div className="unrostered-student-list">
          <div className="unknown-email-box">
            {unknownEmails.map(email => (
              <li key={email} className='selected-course-user'>
                <img src="/assets/blue-cross.svg"
                  alt="Remove"
                  onClick={() => {
                    if (window.controllers && window.controllers.batchAdd) {
                      window.controllers.batchAdd.removeUnknownEmail(email);
                    } else {
                      console.warn("batchAdd controller not found on window.controllers");
                    }
                  }}
                  className="remove-user-icon" />
                <div className='selected-user-name'>{email}</div>
              </li>
            ))}
          </div>
          <div className="info-bubble">
            <span className="tooltip-box small before tooltip-position">
              These students were added by email but they are not yet rostered to your program. They can still be added to your course but will not have access until they have been rostered to your program and complete the registration process. <br/><br/>Once they have been rostered and complete their registration, they will automatically be able to access this course. <br/><br/>For questions, contact {psaEmail}.
            </span>
          </div>
        </div>
      )}

      {rows.filter(row => row.selected).map(row => (
        <li key={row.grdu_id} className='selected-course-user'>
          <img src="/assets/blue-cross.svg"
            alt="Remove"
            onClick={() => toggleSelect([row], false)}
            className="remove-user-icon" />
          <div className='selected-user-name'>
            {row.last_name} {row.first_name}
          </div>
        </li>
      ))}
    </ul>
  )
}
